import { calculateIdealWidths } from '../services/columnWidthCalculator';
import { shrinkColumnsToFit } from '../services/columnWidthDistributor';
import columnResizerMixin from './columnResizerMixin';
import textMeasurementMixin from './textMeasurementMixin';

/**
 * Mixin for managing column widths in a virtual table.
 * Coordinates automatic width calculation, shrinking to fit, and interactive resizing.
 */
export default {
    mixins: [columnResizerMixin, textMeasurementMixin],

    data() {
        return {
            columnWidths: {},
            initialWidthsCalculated: false
        };
    },

    methods: {
        // Recalculates column widths based on content
        // forceRecalculate: If true, ignores manually resized columns and initial calculation flag
        async recalculateColumnWidths(forceRecalculate = false) {
            if (!this.$refs.scrollContainer || !this.headers || this.headers.length === 0) return;

            await this.$nextTick();

            // Once we've calculated widths initially, don't recalculate during loading (prevents header jumping)
            const shouldSkip = !forceRecalculate && this.initialWidthsCalculated && this.loading;
            if (shouldSkip) {
                return;
            }

            const containerWidth = this.$refs.scrollContainer.clientWidth;
            const itemsRef = { value: this.items };
            const totalsRef = { value: this.totals };

            const idealWidths = calculateIdealWidths(this.headers, itemsRef, this.measureText, totalsRef);
            const fittedWidths = shrinkColumnsToFit(idealWidths, containerWidth);

            // Preserve manually resized column widths unless forced
            if (!forceRecalculate && this.manuallyResizedColumns.size > 0) {
                this.columnWidths = this._preserveManualWidths(fittedWidths);
            } else {
                this.columnWidths = fittedWidths;
            }

            this.initialWidthsCalculated = true;
        },

        // Preserves manually resized column widths while using calculated widths for others
        _preserveManualWidths(calculatedWidths) {
            const newWidths = { ...calculatedWidths };

            this.manuallyResizedColumns.forEach((columnKey) => {
                if (this.columnWidths[columnKey] !== undefined) {
                    newWidths[columnKey] = this.columnWidths[columnKey];
                }
            });

            return newWidths;
        },

        // Starts interactive column resizing
        handleResizeStart(column, event) {
            if (!this.$refs.scrollContainer) return;

            const containerWidth = this.$refs.scrollContainer.clientWidth;
            this.startColumnResize(column, event, this.columnWidths, containerWidth, (newWidths) => {
                this.columnWidths = newWidths;
            });
        },

        // Forces a complete recalculation, but preserves manual widths if any exist
        async forceRecalculateWidths() {
            // If we have manually resized columns, preserve them even during forced recalculation
            const shouldPreserveManualWidths = this.manuallyResizedColumns.size > 0;
            await this.recalculateColumnWidths(!shouldPreserveManualWidths);
        },

        // Resets the width calculation state (useful when data completely changes)
        resetColumnWidthState() {
            this.initialWidthsCalculated = false;
            this.columnWidths = {};
        }
    },

    watch: {
        headers: {
            handler(newHeaders, oldHeaders) {
                // Only recalculate if headers actually changed (not just initial setup)
                if (!oldHeaders || oldHeaders.length === 0) {
                    return;
                }
                
                // When headers change structure, we need to recalculate but preserve manual resizes
                const forceRecalc = this.manuallyResizedColumns.size === 0;
                // Reset the initial calculation flag since headers changed
                if (forceRecalc) {
                    this.initialWidthsCalculated = false;
                }
                this.recalculateColumnWidths(forceRecalc);
            },
            deep: true
        }
    }
};
